// Pieces the Structure tab's modes and the constructor share.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Website } from '@/api/types';
import { Icon } from '@/design-system';
import type { GalleryFilters } from './Gallery';
import type { StructureDoc } from './useStructure';
import s from './structure.module.css';

/** What every mode of the tab shares: the site, its structure, the selection, the gallery filters. */
export type StructureCtx = {
  site: Website;
  doc: StructureDoc;
  selected: number;
  select: (i: number) => void;
  filters: GalleryFilters;
  setFilters: (f: GalleryFilters) => void;
  /** opens the full-screen constructor with the current structure */
  openConstructor: () => void;
  /** «Начать с чистой страницы»: clears the structure and opens the constructor */
  openBlank: () => void;
};

export type PrefSummary = { summary: string; swatches: string[]; auto: boolean };

/** «Предпочтения для AI»: sliders, sparkles while everything is on auto (else the palette's 4 swatches), the summary. */
export function PrefsButton({ summary, onClick }: { summary: PrefSummary; onClick: () => void }) {
  const { t } = useTranslation('structure');
  return (
    <button type="button" className={s.prefsBtn} onClick={onClick}>
      <Icon name="sliders-horizontal" size={15} className={s.prefsIcon} />
      <span className={s.nowrap}>{t('prefs.button')}</span>
      {summary.auto && <Icon name="sparkles" size={13} className={s.prefsAuto} />}
      <span className={s.swatches}>
        {summary.swatches.map((c, i) => (
          <span key={i} className={s.swatch} style={{ background: c }} />
        ))}
      </span>
      <span className={s.prefsSum}>{summary.summary}</span>
    </button>
  );
}

/** «ПРЕДПРОСМОТР СТРАНИЦЫ» with its hint line and the expand button (opens the constructor). */
export function PreviewPanel({ hint, onExpand, children }: { hint?: string; onExpand?: () => void; children: ReactNode }) {
  const { t } = useTranslation('structure');
  return (
    <div className={`${s.panel} ${s.listPreview}`}>
      <div className={s.pvHead}>
        <span className={s.caps}>{t('preview.title')}</span>
        <button type="button" className={s.pvExpand} title={t('preview.expand')} aria-label={t('preview.expand')} onClick={onExpand}>
          <Icon name="maximize-2" size={13} />
        </button>
      </div>
      <div className={s.pvHint}>{hint ?? t('preview.hint')}</div>
      <div className={s.pvScroll}>{children}</div>
    </div>
  );
}
